import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PencilIcon, 
  MapPinIcon, 
  BriefcaseIcon, 
  BuildingOfficeIcon,
  CalendarIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { careerService } from '../services/careerService';
import { Career, CareerStatus } from '../types/career';

const CareerDetail: React.FC = () => {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const navigate = useNavigate();
  const [career, setCareer] = useState<Career | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCareer = async () => {
      if (slug) {
        // Public route: fetch by slug
        try {
          const data = await careerService.getCareerBySlug(slug);
          setCareer(data);
        } catch (err) {
          setError('Failed to fetch career details');
        } finally {
          setLoading(false);
        }
      } else if (id) {
        // Admin route: fetch by id
        try {
          const data = await careerService.getCareerById(id);
          setCareer(data);
        } catch (err) {
          setError('Failed to fetch career details');
        } finally {
          setLoading(false);
        }
      } else {
        setError('Career ID or slug is missing');
        setLoading(false);
      }
    };

    fetchCareer();
  }, [id, slug]);

  const getStatusBadge = (status: CareerStatus) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    switch (status) {
      case CareerStatus.PUBLISHED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CareerStatus.DRAFT:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case CareerStatus.UNPUBLISHED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !career) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error || 'Career not found'}
          </div>
          <button
            onClick={() => navigate('/admin/careers')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Careers
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/careers')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Careers
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-start">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">{career.title}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Posted on {formatDate(career.createdAt)}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className={getStatusBadge(career.status)}>
                {career.status}
              </span>
              <Link
                to={`/admin/careers/${career._id}/edit`}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PencilIcon className="mr-1.5 h-4 w-4" />
                Edit
              </Link>
            </div>
          </div>

          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <BuildingOfficeIcon className="mr-2 h-5 w-5 text-gray-400" />
                  Department
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{career.department}</dd>
              </div>

              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPinIcon className="mr-2 h-5 w-5 text-gray-400" />
                  Location
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{career.location}</dd>
              </div>

              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <BriefcaseIcon className="mr-2 h-5 w-5 text-gray-400" />
                  Employment Type
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{career.employmentType}</dd>
              </div>

              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="mr-2 h-5 w-5 text-gray-400" />
                  Application Deadline
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {career.applicationDeadline ? formatDate(career.applicationDeadline) : 'No deadline set'}
                </dd>
              </div>

              {career.salary && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Salary Range</dt>
                  <dd className="mt-1 text-sm text-gray-900">{career.salary}</dd>
                </div>
              )}

              {career.tags && career.tags.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <TagIcon className="mr-2 h-5 w-5 text-gray-400" />
                    Tags
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <div className="flex flex-wrap gap-2">
                      {career.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="space-y-8">
              <div>
                <h4 className="text-lg font-medium text-gray-900">Job Description</h4>
                <div className="mt-2 prose prose-indigo prose-sm text-gray-500">
                  {career.description.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {career.requirements && career.requirements.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Requirements</h4>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-500">
                    {career.requirements.map((requirement, index) => (
                      <li key={index}>{requirement}</li>
                    ))}
                  </ul>
                </div>
              )}

              {career.responsibilities && career.responsibilities.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Responsibilities</h4>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-500">
                    {career.responsibilities.map((responsibility, index) => (
                      <li key={index}>{responsibility}</li>
                    ))}
                  </ul>
                </div>
              )}

              {career.benefits && career.benefits.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Benefits</h4>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-500">
                    {career.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">{career.views || 0}</span> views
                <span className="mx-2">•</span>
                <span className="font-medium text-gray-900">{career.applications || 0}</span> applications
              </div>
              {career.status === CareerStatus.PUBLISHED && career.slug && (
                <a
                  href={`/careers/${career.slug}/apply`}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Apply Now
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CareerDetail; 